
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

const VitiligoPage = () => {
  const symptoms = [
    '皮膚に白い斑点がある',
    '斑点が徐々に広がる',
    '顔や手に白斑がある',
    '髪の毛が白くなる部分がある',
    '日光に当たると目立つ',
    '心理的ストレスを感じる'
  ];

  const causes = [
    {
      title: '紫外線',
      description: '強い紫外線による影響',
      icon: 'ri-sun-line'
    },
    {
      title: 'ストレス',
      description: '精神的ストレスの蓄積',
      icon: 'ri-emotion-unhappy-line'
    },
    {
      title: '自己免疫',
      description: '免疫システムの異常',
      icon: 'ri-shield-line'
    },
    {
      title: '遺伝的要因',
      description: '家族歴による影響',
      icon: 'ri-parent-line'
    }
  ];

  const treatments = [
    {
      category: '漢方薬',
      items: [
        { name: '当帰飲子', effect: '血行促進・肌の再生' },
        { name: '加味逍遙散', effect: '肝気鬱結の改善' },
        { name: '十全大補湯', effect: '気血両虚の改善' }
      ]
    },
    {
      category: '生薬',
      items: [
        { name: '当帰', effect: '血液循環の改善' },
        { name: '川芎', effect: '血行促進' },
        { name: '白芍', effect: '肝の調整' }
      ]
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": "尋常性白斑の漢方治療 - 一心堂薬局",
    "description": "尋常性白斑の症状、原因、漢方での治療法について詳しく解説。一心堂薬局では体質に合わせた漢方薬で根本的な改善を目指します。",
    "url": `${import.meta.env.VITE_SITE_URL}/vitiligo`,
    "mainEntity": {
      "@type": "MedicalCondition",
      "name": "尋常性白斑",
      "alternateName": "じんじょうせいはくはん",
      "description": "皮膚の色素を作るメラノサイトが減少または消失することで、白い斑点が現れる疾患",
      "possibleCause": [
        "紫外線",
        "ストレス",
        "自己免疫の異常",
        "遺伝的要因"
      ],
      "signOrSymptom": [
        "皮膚に白い斑点がある",
        "斑点が徐々に広がる",
        "顔や手に白斑がある",
        "髪の毛が白くなる部分がある"
      ]
    },
    "about": {
      "@type": "MedicalBusiness",
      "name": "一心堂薬局",
      "telephone": "0479-70-6065"
    },
    "specialty": "漢方薬学",
    "medicalAudience": {
      "@type": "Patient"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <SEOHead
        title="尋常性白斑の漢方治療 - 一心堂薬局"
        description="尋常性白斑の症状、原因、漢方での治療法について詳しく解説。一心堂薬局では体質に合わせた漢方薬で根本的な改善を目指します。"
        keywords="尋常性白斑,白斑,漢方治療,一心堂薬局,多古町,千葉県,皮膚疾患,メラノサイト"
        canonicalUrl={`${import.meta.env.VITE_SITE_URL}/vitiligo`}
        ogTitle="尋常性白斑の漢方治療 - 一心堂薬局"
        ogDescription="尋常性白斑の症状、原因、漢方での治療法について詳しく解説。体質に合わせた漢方薬で根本的な改善を目指します。"
        structuredData={structuredData}
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-arrow-left-line text-green-800 text-xl"></i>
              <span className="text-green-800 font-medium">戻る</span>
            </Link>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                <i className="ri-leaf-fill text-white text-sm"></i>
              </div>
              <span className="font-bold text-green-800" style={{fontFamily: 'Pacifico, serif'}}>一心堂薬局</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-6">
            <img 
              src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/ff1d10140c7be2c94dff52e907d2806f.png"
              alt="尋常性白斑のイメージ"
              className="w-full h-48 object-cover rounded-2xl shadow-lg object-top"
            />
          </div>
          <h1 className="text-2xl font-bold text-purple-800 mb-2">
            尋常性白斑<br />
            <span className="text-lg">(じんじょうせいはくはん)</span>
          </h1>
          <p className="text-purple-600 text-lg mb-4">
            漢方で体質改善
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            尋常性白斑は、皮膚の色素を作るメラノサイトが減少または消失することで、白い斑点が現れる疾患です。紫外線・ストレス・自己免疫の異常などが関与するとされます。見た目の変化が心理的ストレスとなることも多く、長期的なケアが大切です。
          </p>
        </div>
      </div>

      {/* Traditional Medicine Approach */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-purple-800 mb-6 text-center">
            漢方での考え方
          </h2>
          <div className="bg-purple-50 rounded-2xl p-6">
            <p className="text-gray-700 leading-relaxed mb-4">
              漢方では「気血両虚」「肝気鬱結」などの体質を整え、血行を促進し、肌の再生力を高める処方が用いられます。
            </p>
            <p className="text-gray-700 leading-relaxed">
              体の巡りを良くし、心身の調和を取り戻すことで改善を目指します。
            </p>
          </div>
        </div>
      </div>

      {/* Symptoms Check */}
      <div className="px-4 py-8 bg-purple-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-purple-800 mb-6 text-center">
            こんな症状はありませんか？
          </h2>
          <div className="space-y-3">
            {symptoms.map((symptom, index) => (
              <label key={index} className="flex items-center space-x-3 p-4 bg-white rounded-xl cursor-pointer hover:bg-purple-50 transition-colors">
                <input 
                  type="checkbox" 
                  className="w-5 h-5 text-purple-600 rounded opacity-0 absolute"
                />
                <div className="w-5 h-5 border-2 border-purple-300 rounded flex items-center justify-center">
                  <i className="ri-check-line text-purple-600 text-sm opacity-0"></i>
                </div>
                <span className="text-gray-700">{symptom}</span>
              </label>
            ))}
          </div>
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-4">
              気になる症状がある場合は、お気軽にご相談ください
            </p>
            <button className="bg-purple-600 text-white py-3 px-8 rounded-full font-medium">
              無料相談を予約
            </button>
          </div>
        </div>
      </div>

      {/* Causes Section */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-purple-800 mb-6 text-center">
            主な原因
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {causes.map((cause, index) => (
              <div key={index} className="bg-purple-50 rounded-2xl p-4 text-center shadow-sm">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className={`${cause.icon} text-purple-600 text-xl`}></i>
                </div>
                <h3 className="font-semibold text-purple-800 mb-2">{cause.title}</h3>
                <p className="text-xs text-gray-600">{cause.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Treatment Options */}
      <div className="px-4 py-8 bg-purple-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-purple-800 mb-6 text-center">
            当薬局での治療法
          </h2>
          <div className="space-y-6">
            {treatments.map((treatment, index) => (
              <div key={index} className="bg-white rounded-2xl p-6">
                <h3 className="font-bold text-purple-800 mb-4 text-center">{treatment.category}</h3>
                <div className="space-y-3">
                  {treatment.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="bg-purple-50 rounded-xl p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-gray-800">{item.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{item.effect}</p>
                        </div>
                        <i className="ri-information-line text-purple-600 text-lg"></i>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lifestyle Tips */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-purple-800 mb-6 text-center">
            日常生活でできること
          </h2>
          <div className="bg-purple-50 rounded-2xl p-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-sun-line text-purple-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">紫外線対策</h4>
                  <p className="text-sm text-gray-600">日焼け止めや帽子で肌を保護しましょう</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-heart-line text-purple-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">ストレス管理</h4>
                  <p className="text-sm text-gray-600">リラックスできる時間を大切にしましょう</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-apple-line text-purple-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">バランスの良い食事</h4>
                  <p className="text-sm text-gray-600">ビタミンやミネラルを意識した食事を</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Consultation CTA */}
      <div className="px-4 py-8 bg-purple-50">
        <div className="max-w-md mx-auto">
          <div className="bg-gradient-to-r from-purple-100 to-purple-50 rounded-2xl p-6 text-center">
            <h3 className="text-xl font-bold text-purple-800 mb-4">
              お一人で悩まずに<br />まずはご相談ください
            </h3>
            <p className="text-sm text-gray-700 mb-6">
              体質や生活習慣に合わせて、最適な治療法をご提案いたします。<br />
              初回相談は無料です。
            </p>
            <div className="space-y-3">
              <button className="w-full bg-purple-600 text-white py-3 px-6 rounded-full font-medium">
                <i className="ri-phone-line mr-2"></i>
                電話で相談予約
              </button>
              <button className="w-full bg-white text-purple-600 py-3 px-6 rounded-full font-medium border border-purple-200">
                <i className="ri-chat-3-line mr-2"></i>
                LINEで相談
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 w-full bg-white border-t border-gray-200 z-50">
        <div className="max-w-md mx-auto">
          <div className="grid grid-cols-5 py-2">
            <Link to="/" className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-home-5-line text-lg"></i>
              <span className="text-xs mt-1">ホーム</span>
            </Link>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-medicine-bottle-line text-lg"></i>
              <span className="text-xs mt-1">業務内容</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-leaf-line text-lg"></i>
              <span className="text-xs mt-1">漢方相談</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-map-pin-line text-lg"></i>
              <span className="text-xs mt-1">アクセス</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-purple-600">
              <i className="ri-contrast-2-fill text-lg"></i>
              <span className="text-xs mt-1">白斑相談</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="h-20"></div>
    </div>
  );
};

export default VitiligoPage;
